// src/components/home/awareness/BarterFiSurveyCampaign.js

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import BarterFiSurveyScreen from '../../popups/BarterFiSurveyScreen';

import {
  dismissBarterFiSurvey,
  getBarterFiSurveyStatus,
  recordBarterFiStakeClick,
  recordBarterFiSurveyOpen,
  submitBarterFiSurvey,
} from '../../../services/barterFiSurveyApi';

import {
  clearAwarenessCooldown,
  hasAwarenessCooldown,
  setAwarenessCooldown,
} from './awarenessStorage';

const CAMPAIGN_KEY =
  'barterfi_survey';

const DEFAULT_OPEN_DELAY_MS = 900;

const SEVEN_DAYS_MS =
  7 * 24 * 60 * 60 * 1000;

const isMainnetEnvironment = () => {
  const hostname = String(
    window.location.hostname || ''
  ).toLowerCase();

  return (
    hostname.includes('barterfi.app') &&
    !hostname.includes('testnet.')
  );
};

const getErrorMessage = (
  error,
  fallback
) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

function BarterFiSurveyCampaign({
  user,

  enabled = false,

  onEligibilityResolved,
  onOpened,
  onFinished,

  openDelayMs = DEFAULT_OPEN_DELAY_MS,
}) {
  const [isOpen, setIsOpen] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [dismissing, setDismissing] =
    useState(false);

  const [
    openingStakeLink,
    setOpeningStakeLink,
  ] = useState(false);

  const [errorMessage, setErrorMessage] =
    useState('');

  const [result, setResult] =
    useState(null);

  const checkedRef = useRef(false);
  const finishedRef = useRef(false);

  const username = String(
    user?.username || ''
  )
    .trim()
    .toLowerCase();

  const accountType = String(
    user?.accountType || 'primary'
  ).toLowerCase();

  const resolveEligibility = useCallback(
    (eligible) => {
      onEligibilityResolved?.({
        campaignKey: CAMPAIGN_KEY,
        eligible: Boolean(eligible),
      });
    },
    [onEligibilityResolved]
  );

  const finishCampaign = useCallback(
    ({
      outcome,
      action = null,
      response = null,
    }) => {
      if (finishedRef.current) {
        return;
      }

      finishedRef.current = true;

      setIsOpen(false);

      onFinished?.({
        campaignKey: CAMPAIGN_KEY,
        outcome,
        action,
        response,
      });
    },
    [onFinished]
  );

  /**
   * Reset all local state when the user changes.
   */
  useEffect(() => {
    setIsOpen(false);
    setSubmitting(false);
    setDismissing(false);
    setOpeningStakeLink(false);
    setErrorMessage('');
    setResult(null);

    checkedRef.current = false;
    finishedRef.current = false;
  }, [username]);

  /**
   * The manager may disable the campaign because another
   * Home drawer or campaign has become active.
   *
   * This must not count as a user dismissal.
   */
  useEffect(() => {
    if (enabled || !isOpen) {
      return;
    }

    setIsOpen(false);
    setSubmitting(false);
    setDismissing(false);
    setOpeningStakeLink(false);
    setErrorMessage('');
    setResult(null);
  }, [
    enabled,
    isOpen,
  ]);

  /**
   * Allow another backend status check during the next
   * Home awareness cycle.
   */
  useEffect(() => {
    if (enabled || isOpen) {
      return;
    }

    checkedRef.current = false;
    finishedRef.current = false;
  }, [
    enabled,
    isOpen,
  ]);

  /**
   * Check backend-authoritative survey eligibility.
   */
  useEffect(() => {
    if (!username) {
      return undefined;
    }

    /*
     * Linked app identities must not receive this
     * personal-user survey.
     */
    if (accountType === 'app') {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    /*
     * V1 is Mainnet-only.
     */
    if (!isMainnetEnvironment()) {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    if (!enabled) {
      return undefined;
    }

    if (checkedRef.current) {
      return undefined;
    }

    checkedRef.current = true;

    /*
     * This local cooldown avoids unnecessary requests.
     * The backend remains the final authority.
     */
    if (
      hasAwarenessCooldown(
        CAMPAIGN_KEY,
        username
      )
    ) {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    let cancelled = false;
    let timerId = null;

    const checkEligibility = async () => {
      try {
        const response =
          await getBarterFiSurveyStatus();

        if (cancelled) {
          return;
        }

        const eligible =
          response?.data?.eligible === true;

        console.log(
          '📋 BarterFi survey eligibility:',
          {
            username,
            eligible,
            reason:
              response?.data?.reason ||
              null,
          }
        );

        resolveEligibility(eligible);

        if (!eligible) {
          setIsOpen(false);

          /*
           * The backend may say the cooldown expired,
           * so remove any stale local cooldown.
           */
          if (
            response?.data?.reason !==
            'DISMISSAL_COOLDOWN'
          ) {
            clearAwarenessCooldown(
              CAMPAIGN_KEY,
              username
            );
          }

          return;
        }

        clearAwarenessCooldown(
          CAMPAIGN_KEY,
          username
        );

        finishedRef.current = false;

        setErrorMessage('');
        setResult(null);
        setIsOpen(true);

        onOpened?.({
          campaignKey: CAMPAIGN_KEY,
        });

        /*
         * The screen is now genuinely visible.
         * Failure to record the open should not throw
         * the user out of the survey.
         */
        try {
          await recordBarterFiSurveyOpen();
        } catch (openError) {
          console.error(
            'Failed to record survey opening:',
            openError
          );
        }
      } catch (error) {
        console.error(
          'Failed to check survey eligibility:',
          error
        );

        if (!cancelled) {
          setIsOpen(false);

          /*
           * Do not trap the Home awareness manager
           * when the survey API is unavailable.
           */
          resolveEligibility(false);
        }
      }
    };

    timerId = window.setTimeout(() => {
      void checkEligibility();
    }, Math.max(
      0,
      Number(openDelayMs) || 0
    ));

    return () => {
      cancelled = true;

      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [
    username,
    accountType,
    enabled,
    openDelayMs,
    onOpened,
    resolveEligibility,
  ]);

  /**
   * Submit all completed answers to the backend.
   */
  const handleSubmit = useCallback(
    async ({
      answers,
      optionalFeedback,
    }) => {
      if (
        submitting ||
        dismissing ||
        openingStakeLink ||
        finishedRef.current
      ) {
        return;
      }

      try {
        setSubmitting(true);
        setErrorMessage('');

        const response =
          await submitBarterFiSurvey({
            answers,
            optionalFeedback,
          });

        setResult(response.data);
        setSubmitting(false);

        clearAwarenessCooldown(
          CAMPAIGN_KEY,
          username
        );
      } catch (error) {
        console.error(
          'Failed to submit BarterFi survey:',
          error
        );

        setSubmitting(false);

        setErrorMessage(
          getErrorMessage(
            error,
            'We could not submit your survey. Please try again.'
          )
        );
      }
    },
    [
      username,
      submitting,
      dismissing,
      openingStakeLink,
    ]
  );

  /**
   * Close the survey.
   *
   * Before submission this records a real dismissal.
   * After submission, Done completes the campaign without
   * creating a dismissal.
   */
  const handleDismiss = useCallback(
    async ({
      completed = false,
    } = {}) => {
      if (
        submitting ||
        dismissing ||
        openingStakeLink ||
        finishedRef.current
      ) {
        return;
      }

      if (completed || result) {
        finishCampaign({
          outcome: 'completed',
          action: 'survey_submitted',
          response: result,
        });

        return;
      }

      try {
        setDismissing(true);
        setErrorMessage('');

        const response =
          await dismissBarterFiSurvey();

        const nextEligibleAt =
          response?.data?.nextEligibleAt
            ? new Date(
                response.data.nextEligibleAt
              ).getTime()
            : null;

        const remainingCooldownMs =
          Number.isFinite(nextEligibleAt)
            ? Math.max(
                0,
                nextEligibleAt -
                  Date.now()
              )
            : 0;

        if (remainingCooldownMs > 0) {
          setAwarenessCooldown({
            campaignKey: CAMPAIGN_KEY,
            username,
            durationMs:
              remainingCooldownMs,
          });
        } else if (
          response?.data
            ?.permanentlyDismissed
        ) {
          /*
           * Backend permanently suppresses this survey.
           * No fake local expiry is required.
           */
          clearAwarenessCooldown(
            CAMPAIGN_KEY,
            username
          );
        } else {
          /*
           * Safe fallback if the backend response did not
           * include its expected cooldown timestamp.
           */
          setAwarenessCooldown({
            campaignKey: CAMPAIGN_KEY,
            username,
            durationMs: SEVEN_DAYS_MS,
          });
        }

        setDismissing(false);

        finishCampaign({
          outcome: 'dismissed',
          action: 'survey_dismissed',
          response:
            response?.data || null,
        });
      } catch (error) {
        console.error(
          'Failed to dismiss BarterFi survey:',
          error
        );

        setDismissing(false);

        setErrorMessage(
          getErrorMessage(
            error,
            'We could not save your choice. Please try again.'
          )
        );
      }
    },
    [
      username,
      result,
      submitting,
      dismissing,
      openingStakeLink,
      finishCampaign,
    ]
  );

  /**
   * Record the click first, then use only the official
   * URL returned by the backend.
   */
  const handleStakeClick =
    useCallback(async () => {
      if (
        submitting ||
        dismissing ||
        openingStakeLink ||
        finishedRef.current ||
        !result?.showStakeInvitation
      ) {
        return;
      }

      try {
        setOpeningStakeLink(true);
        setErrorMessage('');

        const response =
          await recordBarterFiStakeClick();

        const ecosystemUrl =
          response?.data?.ecosystemUrl;

        if (!ecosystemUrl) {
          throw new Error(
            'The official staking page is unavailable.'
          );
        }

        finishCampaign({
          outcome: 'completed',
          action: 'stake_link_clicked',
          response:
            response?.data || null,
        });

        window.location.assign(
          ecosystemUrl
        );
      } catch (error) {
        console.error(
          'Failed to open BarterFi staking:',
          error
        );

        setOpeningStakeLink(false);

        setErrorMessage(
          getErrorMessage(
            error,
            'We could not open the staking page. Please try again.'
          )
        );
      }
    }, [
      result,
      submitting,
      dismissing,
      openingStakeLink,
      finishCampaign,
    ]);

  return (
    <BarterFiSurveyScreen
      open={isOpen}
      submitting={submitting}
      dismissing={dismissing}
      openingStakeLink={
        openingStakeLink
      }
      errorMessage={errorMessage}
      result={result}
      onDismiss={handleDismiss}
      onSubmit={handleSubmit}
      onStakeClick={handleStakeClick}
    />
  );
}

export default BarterFiSurveyCampaign;
